/*
  schmatz-menu.js
  レストランメニューの詳細スライドパネル（モック 260427/260430 の
  「タップしたら、画面の半分にメニューの詳細が出てくる」
  「横スクロールで次のメニューが見れる」を実装）

  - Dawn の作法を踏襲：ネイティブ custom element / trapFocus / removeTrapFocus（global.js）
  - jQuery / slick / splide 不使用。横スクロールは CSS scroll-snap ＋ ネイティブ scroll
  - JS 無効時は details/summary の素の開閉で詳細が読める（このファイルが details を抑止して昇格）
  - パネルの中身は初回タップまで描画しない（タップ時に同じ背景帯グループ分だけ生成）
  - SP は画面下半分、PC は左40%を空けた右側下半分に表示。横スクロールは同一グループの品目を対象にする
*/

(function () {
  'use strict';

  if (customElements.get('menu-detail-panel')) return;

  class MenuDetailPanel extends HTMLElement {
    constructor() {
      super();
      this.openedBy = null;
      this.hideTimer = null;
      this.panelInitialized = false;
    }

    connectedCallback() {
      if (!this.panelInitialized) {
        this.panelInitialized = true;
        this.classList.add('menu-detail-panel');
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.hidden = true;

        this.addEventListener('click', (event) => {
          // 背景（オーバーレイ）クリックで閉じる
          if (event.target === this) this.hide();
        });
        this.addEventListener('keyup', (event) => {
          if (event.code && event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
        this.addEventListener('keydown', (event) => {
          if (event.code === 'ArrowRight') { this.moveBy(1); event.preventDefault(); }
          if (event.code === 'ArrowLeft') { this.moveBy(-1); event.preventDefault(); }
          if (event.code === 'Home') { this.moveTo(0); event.preventDefault(); }
          if (event.code === 'End') { this.moveTo(this.slideCount() - 1); event.preventDefault(); }
        });
      }

    }

    static get() {
      let panel = document.querySelector('menu-detail-panel');
      if (!panel) {
        panel = document.createElement('menu-detail-panel');
      }
      // 接続中の再parentを避け、取得時にbody直下であることを確定する
      if (panel.parentNode !== document.body) document.body.appendChild(panel);
      return panel;
    }

    buildFor(category, targetItem) {
      const label = category.dataset.categoryLabel || '';
      const group = category.dataset.menuPanelGroup || category.dataset.panelGroup || '';
      this.setAttribute('aria-label', label);
      this.innerHTML = '';

      const sheet = document.createElement('div');
      sheet.className = 'menu-detail-panel__sheet';

      const header = document.createElement('div');
      header.className = 'menu-detail-panel__header';

      const title = document.createElement('p');
      title.className = 'menu-detail-panel__title';
      title.textContent = label;

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'menu-detail-panel__close';
      closeBtn.setAttribute('aria-label', '閉じる');
      closeBtn.innerHTML = '&times;';
      closeBtn.addEventListener('click', () => this.hide());

      header.appendChild(title);
      header.appendChild(closeBtn);

      const track = document.createElement('div');
      track.className = 'menu-detail-panel__track';
      track.setAttribute('tabindex', '0');
      track.setAttribute('role', 'list');

      const categories = Array.prototype.filter.call(
        document.querySelectorAll('[data-menu-panel-group], schmatz-menu-category[data-panel-group]'),
        (entry) => (entry.dataset.menuPanelGroup || entry.dataset.panelGroup || '') === group
      );
      const items = Array.prototype.flatMap.call(categories, (entry) =>
        Array.prototype.slice.call(entry.querySelectorAll('[data-menu-item]'))
      );
      let targetSlide = null;
      items.forEach((item, index) => {
        const slide = document.createElement('div');
        slide.className = 'menu-detail-panel__slide';
        slide.setAttribute('role', 'listitem');

        const photo = item.querySelector('[data-menu-photo], .schmatz-menu__item-photo');
        if (photo) {
          const photoWrap = document.createElement('div');
          photoWrap.className = 'menu-detail-panel__photo';
          const imageRatio = photo.dataset.imageRatio || '4 / 3';
          photoWrap.style.setProperty('--schmatz-menu-image-ratio', imageRatio);
          if (imageRatio === 'auto') photoWrap.classList.add('menu-detail-panel__photo--auto');
          photoWrap.innerHTML = photo.innerHTML;
          slide.appendChild(photoWrap);
        }

        const detail = item.querySelector('[data-menu-detail], .menu-item__detail');
        if (detail) slide.appendChild(detail.cloneNode(true));

        const itemName = item.querySelector('[data-menu-name], .menu-item__detail-en, .schmatz-menu__item-en');
        slide.setAttribute('aria-label', `${index + 1}/${items.length}${itemName ? ` ${itemName.textContent.trim()}` : ''}`);

        track.appendChild(slide);
        if (item === targetItem) targetSlide = slide;
      });

      if (items.length > 1) {
        track.setAttribute('aria-label', `${label}の詳細。左右矢印キーで次の品目へ移動できます`);
      }

      sheet.appendChild(header);
      sheet.appendChild(track);
      this.appendChild(sheet);
      this.track = track;
      return targetSlide;
    }

    openFor(category, targetItem) {
      if (this.hideTimer) {
        window.clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
      if (this.openedBy) this.openedBy.setAttribute('aria-expanded', 'false');
      const targetSlide = this.buildFor(category, targetItem);
      this.openedBy = targetItem.querySelector('summary');
      this.hidden = false;
      document.body.classList.add('menu-detail-panel-open');
      // 開いたカードの aria-expanded を同期
      if (this.openedBy) this.openedBy.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        requestAnimationFrame(() => {
          if (targetSlide) this.track.scrollLeft = this.slideOffset(targetSlide);
          if (typeof trapFocus === 'function') trapFocus(this, this.track);
        });
      });
    }

    hide() {
      if (this.hidden) return;
      this.classList.remove('is-open');
      document.body.classList.remove('menu-detail-panel-open');
      if (this.openedBy) this.openedBy.setAttribute('aria-expanded', 'false');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
      this.openedBy = null;
      // トランジション終了後に非表示（安全のためタイムアウト併用）
      if (this.hideTimer) window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => {
        if (!this.classList.contains('is-open')) this.hidden = true;
        this.hideTimer = null;
      }, 300);
    }

    slideCount() {
      return this.track ? this.track.children.length : 0;
    }

    slideOffset(slide) {
      if (!this.track || !slide) return 0;
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      return this.track.scrollLeft + slideRect.left - trackRect.left;
    }

    currentIndex() {
      if (!this.track || !this.track.children.length) return 0;
      const slides = Array.prototype.slice.call(this.track.children);
      let nearest = 0;
      let min = Infinity;
      slides.forEach((slide, i) => {
        const d = Math.abs(this.slideOffset(slide) - this.track.scrollLeft);
        if (d < min) { min = d; nearest = i; }
      });
      return nearest;
    }

    moveBy(delta) {
      this.moveTo(this.currentIndex() + delta);
    }

    moveTo(index) {
      if (!this.track) return;
      const slides = this.track.children;
      if (!slides.length) return;
      const clamped = Math.max(0, Math.min(slides.length - 1, index));
      this.track.scrollTo({ left: this.slideOffset(slides[clamped]), behavior: 'smooth' });
    }
  }
  customElements.define('menu-detail-panel', MenuDetailPanel);

  class SchmatzMenuCategory extends HTMLElement {
    connectedCallback() {
      if (this.menuClickHandler) return;
      this.querySelectorAll('summary').forEach((summary) => summary.setAttribute('aria-expanded', 'false'));
      this.menuClickHandler = (event) => {
        const summary = event.target.closest('summary');
        if (!summary || !this.contains(summary)) return;
        const item = summary.closest('[data-menu-item]');
        if (!item) return;
        // ネイティブ details の開閉を抑止し、詳細パネルへ昇格
        event.preventDefault();
        MenuDetailPanel.get().openFor(this, item);
      };
      this.addEventListener('click', this.menuClickHandler);
    }

    disconnectedCallback() {
      if (!this.menuClickHandler) return;
      this.removeEventListener('click', this.menuClickHandler);
      this.menuClickHandler = null;
    }
  }
  customElements.define('schmatz-menu-category', SchmatzMenuCategory);
})();
